import { timeFormatOptions } from '@/utils/date';

// Formats an ISO instant in the viewer's own timezone through `Intl`. Every function
// takes `now` so a caller (and a test) can pin "today" instead of reading the system clock.
// `prefer12Hr` is the app's Time Format setting; `null` leaves the clock to the locale.

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dayBefore(date: Date): Date {
  const before = new Date(date);
  before.setDate(before.getDate() - 1);
  return before;
}

export function formatClockTime(
  date: Date,
  prefer12Hr: boolean | null,
  timeZone?: string
): string {
  const options = timeFormatOptions(prefer12Hr);
  if (prefer12Hr === false) {
    delete options.hour12;
    options.hourCycle = 'h23';
  }
  return date
    .toLocaleTimeString([], { ...options, timeZone })
    .replace(/\b(am|pm)\b/gi, (period) => period.toUpperCase());
}

// Today shows the time alone; yesterday and anything earlier add a day marker.
export function formatMessageTime(
  iso: string,
  prefer12Hr: boolean | null,
  now: Date = new Date()
): string {
  const date = new Date(iso);
  const time = formatClockTime(date, prefer12Hr);
  if (isSameCalendarDay(date, now)) return time;
  if (isSameCalendarDay(date, dayBefore(now))) return `Yesterday ${time}`;
  const day = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${day} ${time}`;
}

// The "8m ago" half of a relative time line; the caller supplies any leading verb.
export function formatRelativeTime(
  iso: string,
  now: Date = new Date()
): string {
  const elapsedMs = Math.max(0, now.getTime() - new Date(iso).getTime());
  if (elapsedMs < MINUTE_MS) return 'just now';
  if (elapsedMs < HOUR_MS) return `${Math.floor(elapsedMs / MINUTE_MS)}m ago`;
  if (elapsedMs < DAY_MS) return `${Math.floor(elapsedMs / HOUR_MS)}h ago`;
  return `${Math.floor(elapsedMs / DAY_MS)}d ago`;
}

// The full date, time and timezone, for a hover title or long-press.
export function formatFullDateTime(
  iso: string,
  prefer12Hr: boolean | null
): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const zone = new Intl.DateTimeFormat([], { timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;
  return `${day}, ${formatClockTime(date, prefer12Hr)} ${zone ?? ''}`.trim();
}
